import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

export interface PageLayoutProps {
  /**
   * Usually a <PageHeader> or <EntityHeader>. In suite it sticks to the top on a raised band while
   * the body scrolls; in berry it sits on the page background at the top of the body.
   */
  readonly header?: ReactNode;
  /** Optional band under the header — tabs, filter bar, as-of banner. */
  readonly subHeader?: ReactNode;
  readonly children: ReactNode;
  /** Constrains reading width for form-heavy pages; grids want the full width. */
  readonly maxWidth?: number | 'none';
}

/**
 * The frame every screen sits in. One layout means vertical rhythm, gutters and scroll behaviour
 * are decided once instead of per screen — which is what makes twelve screens by three teams
 * look like one application (backbone §6).
 *
 * Two treatments, chosen by the theme (`theme.eis.surface.headerBand`), never by the screen:
 *   - suite keeps prompt 22's sticky header band on `surfaces.raised`;
 *   - berry (EIS Design System, prompt 24 Phase 24.3) has no band — the title, the header card and
 *     the tabs stack on the page background with the content, padded 24/32, 24 apart.
 */
export function PageLayout({
  header,
  subHeader,
  children,
  maxWidth = 'none',
}: PageLayoutProps): ReactNode {
  const banded = useTheme().eis.surface.headerBand;

  const content = (
    <Stack
      spacing={banded ? 2.5 : 3}
      sx={{
        maxWidth: maxWidth === 'none' ? '100%' : maxWidth,
        width: '100%',
        mx: maxWidth === 'none' ? 0 : 'auto',
        minHeight: 0,
      }}
    >
      {!banded && header}
      {!banded && subHeader}
      {children}
    </Stack>
  );

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      {banded && header !== undefined && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'surfaces.raised',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ px: 3, pt: 2, pb: subHeader === undefined ? 2 : 0 }}>{header}</Box>
          {subHeader !== undefined && <Box sx={{ px: 3 }}>{subHeader}</Box>}
        </Box>
      )}
      {/*
        The scrolling region, and it is FOCUSABLE.

        A region that scrolls but holds nothing focusable cannot be scrolled from the keyboard at
        all (WCAG 2.1.1, axe `scrollable-region-focusable`) — which is exactly what a long
        read-only screen is: a detail page of headings and definition lists has no control to tab
        to. `tabIndex={0}` puts the region itself in the tab order so the arrow keys reach it.
      */}
      <Box
        tabIndex={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: banded ? 3 : 4,
          py: banded ? 2.5 : 3,
        }}
      >
        {content}
      </Box>
    </Box>
  );
}
